import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Connects to data-controller="eid-import"
export default class extends Controller {
  static targets = ["button", "feedback", "instructions"]
  // Affiche ou masque la zone d'instructions
  toggleInstructions() {
    if (!this.hasInstructionsTarget) return
    const el = this.instructionsTarget
    el.style.display = (el.style.display === 'none' || el.style.display === '') ? 'block' : 'none'
  }
  static values = {
    terminalId: String
  }

  connect() {
    console.log("EidImportController connected")
    console.log("Terminal ID:", this.terminalIdValue)

    // Expose controller globally for testing and debugging
    window.eidImportController = this

    this.isImporting = false
    this.createHubStatusSubscription()
    this.createEidImportSubscription()
  }

  disconnect() {
    if (this.hubStatusSubscription) {
      this.hubStatusSubscription.unsubscribe()
    }
    if (this.eidImportSubscription) {
      this.eidImportSubscription.unsubscribe()
    }
  }

  createHubStatusSubscription() {
    console.log("Creating HubChannel subscription for eID import...")
    this.hubStatusSubscription = consumer.subscriptions.create(
      {
        channel: "HubChannel",
        terminal_id: this.terminalIdValue,
        client_type: "browser"
      },
      {
        connected() {
          console.log("EidImport HubChannel connected!")
        },

        disconnected() {
          console.log("EidImport HubChannel disconnected!")
        },

        rejected() {
          console.log("EidImport HubChannel subscription rejected!")
        },

        received: (data) => {
          console.log("EidImport HubChannel received data:", data)
          this.updateConnectionStatus(data.terminal_status === 'ready')
        }
      }
    )
  }

  createEidImportSubscription() {
    console.log("Creating EidImportChannel subscription...")
    this.eidImportSubscription = consumer.subscriptions.create(
      {
        channel: "EidImportChannel"
      },
      {
        connected() {
          console.log("EidImportChannel connected!")
        },

        disconnected() {
          console.log("EidImportChannel disconnected!")
        },

        rejected() {
          console.log("EidImportChannel subscription rejected!")
        },

        received: (data) => {
          console.log("EidImportChannel received data:", data)
          this.handleEidData(data)
        }
      }
    )
  }

  requestImport() {
    if (this.isImporting) {
      console.log("Import already in progress")
      return
    }

    if (!this.isHubConnected) {
      this.showError("Le Local Device Hub n'est pas connecté. Veuillez vérifier qu'il est bien lancé.")
      return
    }

    console.log("Requesting eID import...")
    this.isImporting = true
    this.showImportingState()

    // Request import via the EidImportChannel
    this.eidImportSubscription.perform('request_import', {
      terminal_id: this.terminalIdValue
    })

    // Set a timeout to reset the state if no response is received
    this.importTimeout = setTimeout(() => {
      if (this.isImporting) {
        this.showError("Délai d'attente dépassé. Veuillez réessayer.")
        this.resetImportState()
      }
    }, 30000) // 30 seconds timeout
  }

  handleEidData(data) {
    console.log("Handling eID data:", data)

    if (this.importTimeout) {
      clearTimeout(this.importTimeout)
      this.importTimeout = null
    }

    if (data.status === 'success' && data.data) {
      this.fillFormWithEidData(data.data)
      this.showSuccess("Données de la carte d'identité importées avec succès.")
    } else {
      this.showError(data.message || "Erreur lors de la lecture de la carte d'identité.")
    }

    this.resetImportState()
  }

  fillFormWithEidData(data) {
    console.log("Filling form with eID data:", data)

    // Fill form fields with imported data
    const fields = {
      'client_first_name': data.first_name,
      'client_last_name': data.last_name,
      'client_national_number': data.national_number,
      'client_id_card_number': data.id_card_number,
      'client_street': data.street_and_number,
      'client_zip_code': data.postal_code,
      'client_city': data.municipality,
      'client_country': data.country,
      'client_birth_date': data.birth_date
    }

    Object.entries(fields).forEach(([fieldName, value]) => {
      const field = document.getElementById(fieldName) || document.querySelector(`[name="${fieldName}"]`)
      if (field && value) {
        field.value = value
        // Trigger change event to ensure any form validation or other listeners are notified
        field.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })

    // Handle photo if present
    if (data.photo_base64) {
      this.handlePhotoImport(data.photo_base64)
    }
  }

  handlePhotoImport(photoBase64) {
    console.log("Photo data received:", photoBase64.substring(0, 50) + "...")

    try {
      // Chercher le contrôleur files-upload dans la page
      const uploadController = this.findFilesUploadController()

      if (uploadController && uploadController.uppy) {
        // Convertir le base64 en Blob
        const photoBlob = this.base64ToBlob(photoBase64, 'image/jpeg')

        // Créer un fichier pour Uppy avec un nom unique
        const fileId = `eid-photo-${Date.now()}`
        const fileName = `photo-eid-${Date.now()}.jpg`

        // Supprimer tous les fichiers existants d'abord
        const existingFiles = uploadController.uppy.getFiles()
        existingFiles.forEach(file => {
          uploadController.uppy.removeFile(file.id)
        })

        // Ajouter le nouveau fichier à Uppy
        uploadController.uppy.addFile({
          id: fileId,
          name: fileName,
          type: 'image/jpeg',
          data: photoBlob,
          source: 'eID Import',
          isRemote: false
        })

        console.log("Photo successfully added to Uppy")

        // Déclencher l'upload automatiquement si configuré
        if (uploadController.autoUploadValue) {
          setTimeout(() => {
            uploadController.uppy.upload()
          }, 100)
        }

      } else {
        console.warn("Files upload controller not found or not initialized")
        this.fallbackPhotoDisplay(photoBase64)
      }
    } catch (error) {
      console.error("Error handling photo import:", error)
      this.fallbackPhotoDisplay(photoBase64)
    }
  }

  // Méthode utilitaire pour trouver le contrôleur files-upload
  findFilesUploadController() {
    const uploadElement = document.querySelector('[data-controller*="files-upload"]')
    if (uploadElement && uploadElement.filesUploadController) {
      return uploadElement.filesUploadController
    }

    // Fallback: chercher via Stimulus
    if (window.application && window.application.controllers) {
      for (const controller of window.application.controllers) {
        if (controller.identifier === 'files-upload' && controller.uppy) {
          return controller
        }
      }
    }

    return null
  }

  // Méthode utilitaire pour convertir base64 en Blob
  base64ToBlob(base64, mimeType) {
    const byteCharacters = atob(base64.split(',')[1] || base64)
    const byteNumbers = new Array(byteCharacters.length)

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: mimeType })
  }

  // Méthode de fallback si Uppy n'est pas disponible
  fallbackPhotoDisplay(photoBase64) {
    // Si l'intégration Uppy échoue, afficher dans un aperçu simple
    const photoPreview = document.querySelector('[data-photo-preview]')
    if (photoPreview) {
      photoPreview.src = photoBase64
      photoPreview.style.display = 'block'
    }
  }

  updateConnectionStatus(isReady) {
    console.log(`EidImport updateConnectionStatus called with: ${isReady}`)
    this.isHubConnected = isReady

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = !isReady
      if (isReady) {
        this.buttonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.buttonTarget.title = "Cliquez pour importer les données de la carte d'identité"
      } else {
        this.buttonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        this.buttonTarget.title = "Le Local Device Hub n'est pas détecté. Veuillez vérifier qu'il est bien lancé."
      }
    }
  }

  showImportingState() {
    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        En attente de la carte...
      `
    }

    this.showFeedback("Veuillez insérer la carte d'identité dans le lecteur.", "info")
  }

  resetImportState() {
    this.isImporting = false

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = !this.isHubConnected
      this.buttonTarget.innerHTML = `
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V4a2 2 0 114 0v2m-4 0a2 2 0 104 0m-4 0V4a2 2 0 014 0v2"></path>
        </svg>
        Importer depuis l'eID
      `
    }
  }

  showFeedback(message, type = "info") {
    if (!this.hasFeedbackTarget) return

    const bgColor = type === "error" ? "bg-red-50 border-red-200" :
      type === "success" ? "bg-green-50 border-green-200" :
        "bg-blue-50 border-blue-200"

    const textColor = type === "error" ? "text-red-800" :
      type === "success" ? "text-green-800" :
        "text-blue-800"

    this.feedbackTarget.innerHTML = `
      <div class="rounded-md ${bgColor} border p-4">
        <p class="${textColor}">${message}</p>
      </div>
    `
    this.feedbackTarget.style.display = 'block'

    // Auto-hide info and success messages after 5 seconds
    if (type !== "error") {
      setTimeout(() => {
        if (this.hasFeedbackTarget) {
          this.feedbackTarget.style.display = 'none'
        }
      }, 5000)
    }
  }

  showError(message) {
    this.showFeedback(message, "error")
  }

  showSuccess(message) {
    this.showFeedback(message, "success")
  }

  hideFeedback() {
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.style.display = 'none'
    }
  }
}
